import React, { useRef, useCallback, useState } from 'react';

import ShapeTreeRenderer from './ShapeTreeRenderer.jsx';
import useUmlSchema from '../../useUmlSchema.js';
import umlCanvasEngine from '../../Core/UmlCanvasEngine.js';
import { resolveInterfaceReduction } from '../../Core/resolveInterfaceReduction.js';
import { resolveUserHiddenItems } from '../../Core/resolveUserHiddenItems.js';
import { umlConnectorEngine } from '@jxUml';
import UmlConnectorLayer from './UmlConnectorLayer.jsx';
import UmlCanvasToolbar from './UmlCanvasToolbar.jsx';
import UmlWorldMap from './UmlWorldMap.jsx';
import UmlHiddenItemsDlg from './UmlHiddenItemsDlg/UmlHiddenItemsDlg.jsx';

import useViewportInteractions from './UmlCanvas.useViewportInteractions.js';
import useConnectorDrafting from './UmlCanvas.useConnectorDrafting.js';
import useAnchorReattach from './UmlCanvas.useAnchorReattach.js';
import useShapeDeleteShortcut from './UmlCanvas.useShapeDeleteShortcut.js';
import useHoveredShapeId from './UmlCanvas.useHoveredShapeId.js';
import useConnectorKeyboardShortcuts from './UmlCanvas.useConnectorKeyboardShortcuts.js';
import useHoverDebugLogging from './UmlCanvas.useHoverDebugLogging.js';

import './UmlCanvas.css';

/**
 * Canvas workspace: pan/zoom transform, renders shapes + connector overlay.
 * Ctrl(+wheel) = zoom-to-cursor, Ctrl+mousedown on a shape = draw a connector,
 * Ctrl+drag on a connector anchor = reattach it to another shape.
 */
function UmlCanvas() {
    const {
        shapes,
        connectors,
        viewConfig,
        viewport,
        moveShape,
        moveSeqMessageLabel,
        resizeShape,
        movePoint,
        renameShape,
        toggleShapeCollapsed,
        setShapeIconKind,
        setDerivedLabelOffset,
        getShapeById,
        selectedId,
        selectedShapeIds,
        selectShape,
        shiftSelectShape,
        clearSelection,
    } = useUmlSchema();
    // Chained: user-hidden items first (the more fundamental manual toggle), then
    // interface-reduction operates on whatever remains visible after that, then
    // collapsed-package anchor redirection operates on the final visible set.
    const { visibleShapes: shapesAfterHidden, visibleConnectors: connectorsAfterHidden } = resolveUserHiddenItems(
        shapes,
        connectors,
        viewConfig,
    );
    const { visibleShapes, visibleConnectors: connectorsAfterReduction } = resolveInterfaceReduction(shapesAfterHidden, connectorsAfterHidden, viewConfig);
    const visibleConnectors = umlConnectorEngine.resolveCollapsedPackageAnchors(visibleShapes, connectorsAfterReduction);
    const canvasRef = useRef(null);
    // Ref, not state — a shape can move to a different React parent mid-drag
    // (package escape/adopt), which remounts the widget and would wipe state.
    const draggingShapeIdRef = useRef(null);
    const [worldMapOpen, setWorldMapOpen] = useState(true);
    // Bound to this UmlWorkspace instance (not a jxWindowManager global window,
    // see UmlHiddenItemsDlg.jsx doc) — same mount/unmount-via-local-state pattern
    // as worldMapOpen above.
    const [hiddenItemsDlgOpen, setHiddenItemsDlgOpen] = useState(false);
    // Portal target for package titles (see .uml-package-title-layer below) — state,
    // not a plain ref: the DOM node doesn't exist yet on the first render, so this
    // needs to trigger a re-render once the div actually mounts (standard React
    // "ref callback via setState" recipe for portal targets rendered later in the tree).
    const [titleLayerNode, setTitleLayerNode] = useState(null);

    const handleToggleWorldMap = useCallback(function toggleWorldMap() {
        setWorldMapOpen(function flip(open) {
            return !open;
        });
    }, []);

    const handleCloseWorldMap = useCallback(function closeWorldMap() {
        setWorldMapOpen(false);
    }, []);

    const handleToggleHiddenItems = useCallback(function toggleHiddenItems() {
        setHiddenItemsDlgOpen(function flip(open) {
            return !open;
        });
    }, []);

    const handleCloseHiddenItems = useCallback(function closeHiddenItems() {
        setHiddenItemsDlgOpen(false);
    }, []);

    const { toWorldPoint, handleBackgroundPointerDown, handleBackgroundPointerMove, handleBackgroundPointerUp } = useViewportInteractions(
        canvasRef,
        clearSelection,
    );
    const { draftPoints, handleConnectorDragStart, handleConnectorDoubleClick, handleConnectorPointRemove, handleConnectorDelete } =
        useConnectorDrafting(toWorldPoint, getShapeById);
    const { reattachDraft, reattachStart, handleAnchorDragStart } = useAnchorReattach(toWorldPoint, getShapeById);
    const { handleWorldMouseOver } = useShapeDeleteShortcut();
    const { hoveredShapeId, hoveredConnectorId, hoveredGeo, handleWorldMouseOverForDisplay } = useHoveredShapeId();

    useConnectorKeyboardShortcuts(connectors, visibleConnectors, selectedId, getShapeById, shapes);
    useHoverDebugLogging(hoveredShapeId, hoveredConnectorId, hoveredGeo, connectors, getShapeById);

    const handleWorldMouseOverCombined = useCallback(
        function combinedWorldMouseOver(event) {
            handleWorldMouseOver(event);
            handleWorldMouseOverForDisplay(event);
        },
        [handleWorldMouseOver, handleWorldMouseOverForDisplay],
    );

    const handleShapeBroughtToFront = useCallback(function bringShapeToFront(shapeId) {
        umlCanvasEngine.bringShapeToFront(shapeId);
    }, []);

    // Layered z-order: packages (back) -> connectors (middle) -> classes/comments (front), so a
    // connector never cuts across a class's caption, but does sit in front of package borders.
    // ShapeTreeRenderer.renderLayered walks the whole shape tree (incl. nested package children)
    // into these 2 buckets.
    const { packageElements, otherElements } = ShapeTreeRenderer.renderLayered(
        visibleShapes,
        moveShape,
        resizeShape,
        handleConnectorDragStart,
        renameShape,
        selectedId,
        selectShape,
        handleShapeBroughtToFront,
        toggleShapeCollapsed,
        setShapeIconKind,
        toWorldPoint,
        draggingShapeIdRef,
        titleLayerNode,
        selectedShapeIds,
        shiftSelectShape,
        getShapeById,
        0,
        moveSeqMessageLabel,
    );

    return (
        <div
            ref={canvasRef}
            className="uml-canvas"
            onPointerDown={handleBackgroundPointerDown}
            onPointerMove={handleBackgroundPointerMove}
            onPointerUp={handleBackgroundPointerUp}
        >
            {/* Outside uml-canvas-world, so no pan/zoom transform — stays fixed
          top-left regardless of scroll/zoom state. */}
            <UmlCanvasToolbar
                hoveredShapeId={hoveredShapeId}
                hoveredConnectorId={hoveredConnectorId}
                hoveredGeo={hoveredGeo}
                connectors={connectors}
                visibleConnectors={visibleConnectors}
                worldMapOpen={worldMapOpen}
                onToggleWorldMap={handleToggleWorldMap}
                hiddenItemsOpen={hiddenItemsDlgOpen}
                onToggleHiddenItems={handleToggleHiddenItems}
                canvasRef={canvasRef}
            />
            {worldMapOpen && <UmlWorldMap onClose={handleCloseWorldMap} />}
            {hiddenItemsDlgOpen && <UmlHiddenItemsDlg onClose={handleCloseHiddenItems} />}

            <div
                className="uml-canvas-world"
                style={{ transform: `translate(${viewport.panX}px, ${viewport.panY}px) scale(${viewport.scale})` }}
                onMouseOver={handleWorldMouseOverCombined}
            >
                {/* Layered z-order, see comment above where packageElements/otherElements are computed —
                    each block is wrapped in its own stacking-context div (.uml-package-layer /
                    .uml-shape-layer), see UmlCanvas.css + UmlConnectorLayer.css for why that's
                    needed (a flat CSS z-index on the connector layer previously always won over
                    shapes regardless of DOM order). */}
                <div className="uml-package-layer">{packageElements}</div>
                <UmlConnectorLayer
                    connectors={visibleConnectors}
                    getShapeById={getShapeById}
                    selectedId={selectedId}
                    onPointMoved={movePoint}
                    onLabelMoved={movePoint}
                    onDerivedLabelMoved={setDerivedLabelOffset}
                    onConnectorDoubleClick={handleConnectorDoubleClick}
                    onPointRemove={handleConnectorPointRemove}
                    onAnchorDragStart={handleAnchorDragStart}
                    onConnectorDelete={handleConnectorDelete}
                    toWorldPoint={toWorldPoint}
                />
                {/* Portal target for package titles (z-index 15, between connectors and shapes)
                    — see UmlPackageWidget.jsx/UmlCanvas.css. Keeps package captions readable
                    even where a connector line crosses the package. */}
                <div className="uml-package-title-layer" ref={setTitleLayerNode} />
                <div className="uml-shape-layer">{otherElements}</div>
                {draftPoints.length > 1 && (
                    <svg className="uml-connector-draft-layer">
                        <polyline
                            points={draftPoints
                                .map(function toPointStr(p) {
                                    return `${p.x},${p.y}`;
                                })
                                .join(' ')}
                            fill="none"
                            stroke="#66b2ff"
                            strokeWidth={1.5}
                            strokeDasharray="4 3"
                        />
                    </svg>
                )}
                {reattachStart && (
                    <svg className="uml-connector-draft-layer">
                        <line
                            x1={reattachStart.x}
                            y1={reattachStart.y}
                            x2={reattachDraft.x}
                            y2={reattachDraft.y}
                            stroke="#66b2ff"
                            strokeWidth={1.5}
                            strokeDasharray="4 3"
                        />
                    </svg>
                )}
            </div>
        </div>
    );
}

export default UmlCanvas;
